import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  Loader2,
  RefreshCw,
  ChevronsUpDown,
  Check,
  Clock,
  Package,
  CheckCircle2,
  AlertCircle,
  FileText,
  Timer,
  Filter,
} from 'lucide-react';
import { usePapSync } from '@/features/organization/hooks/usePapSync';
import { PapSyncStatus } from './PapSyncStatus';
import { SyncStat } from './sync-ui-utils';
import { trpc } from '@/lib/trpc';
import { formatDistanceToNow } from 'date-fns';

export function PapSyncCard() {
  const { startSync, isSyncing, papSync } = usePapSync();
  const [productType, setProductType] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [syncRange, setSyncRange] = useState<string>(() =>
    papSync?.lastCompletedAt ? 'since_last' : 'full'
  );
  const productTypesQuery = trpc.productionSettings.getDistinctProductTypes.useQuery();

  useEffect(() => {
    setSyncRange(papSync?.lastCompletedAt ? 'since_last' : 'full');
  }, [papSync?.lastCompletedAt]);

  const handleSync = () => {
    startSync(
      productType || undefined,
      syncRange as 'full' | 'since_last' | '7d' | '30d' | '90d',
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <Package className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Product Sync</CardTitle>
            <CardDescription className="text-xs">
              Syncs product catalog from Shopify every 4 hours.
              {' '}Exclude products with the <code className="text-[0.8em] bg-muted px-1 py-0.5 rounded font-mono">exclude-printrun</code> tag
              (the older <code className="text-[0.8em] bg-muted px-1 py-0.5 rounded font-mono">exclude-erp</code> still works).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            Every 4 hours
          </Badge>
          <PapSyncStatus papSync={papSync} isSyncing={isSyncing} />
        </div>

        {/* Controls row */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pap-product-type" className="text-xs text-muted-foreground">Product Type</Label>
            <Popover open={typeOpen} onOpenChange={setTypeOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="pap-product-type"
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={typeOpen}
                  className="h-8 w-44 justify-between text-xs"
                >
                  {productType || 'All types'}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search types..." className="h-8 text-xs" />
                  <CommandList>
                    <CommandEmpty>No types found</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value=""
                        onSelect={() => { setProductType(''); setTypeOpen(false); }}
                      >
                        <Check className={cn('mr-2 h-3.5 w-3.5', !productType ? 'opacity-100' : 'opacity-0')} />
                        All types
                      </CommandItem>
                      {(productTypesQuery.data?.productTypes ?? []).map((pt) => (
                        <CommandItem
                          key={pt}
                          value={pt}
                          onSelect={() => { setProductType(pt === productType ? '' : pt); setTypeOpen(false); }}
                        >
                          <Check className={cn('mr-2 h-3.5 w-3.5', productType === pt ? 'opacity-100' : 'opacity-0')} />
                          {pt}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">Sync Range</Label>
            <Select value={syncRange} onValueChange={setSyncRange}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {papSync?.lastCompletedAt && (
                  <SelectItem value="since_last">Since last sync</SelectItem>
                )}
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="full">Full sync</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
            className="h-8"
          >
            {isSyncing ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Syncing...</>
            ) : (
              <><RefreshCw className="mr-1.5 h-3.5 w-3.5" />{productType ? `Sync ${productType}` : 'Sync Products'}</>
            )}
          </Button>
        </div>

        {/* Active sync progress */}
        {isSyncing && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-3 space-y-2.5">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {papSync?.status === 'running' && papSync.progress ? 'Syncing products...' : 'Starting sync...'}
            </div>
            {papSync?.status === 'running' && papSync.progress && (
              <div className="flex flex-wrap gap-2">
                <SyncStat label="products" value={papSync.progress.productsProcessed} icon={Package} />
                <SyncStat label="SKUs upserted" value={papSync.progress.skusUpserted} icon={CheckCircle2} />
                <SyncStat label="pages" value={papSync.progress.pagesProcessed} icon={FileText} />
              </div>
            )}
          </div>
        )}

        {/* Last sync error */}
        {!isSyncing && papSync?.status === 'failed' && papSync.error && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-red-700 dark:text-red-300">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Sync failed
            </div>
            <p className="mt-1 text-xs text-red-600 dark:text-red-400 pl-6">{papSync.error}</p>
          </div>
        )}

        {/* Last sync results */}
        {!isSyncing && papSync?.status !== 'failed' && papSync?.progress && papSync.lastCompletedAt && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2.5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Last sync</span>
              {papSync.source && <Badge variant="muted" size="sm">{papSync.source}</Badge>}
              {papSync.productType && (
                <Badge variant="muted" size="sm">
                  <Filter className="mr-1 h-2.5 w-2.5" />{papSync.productType}
                </Badge>
              )}
              <span className="ml-auto">
                {formatDistanceToNow(new Date(papSync.lastCompletedAt), { addSuffix: true })}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <SyncStat label="products" value={papSync.progress.productsProcessed} icon={Package} />
              <SyncStat label="SKUs upserted" value={papSync.progress.skusUpserted} icon={CheckCircle2} />
              <SyncStat label="pages" value={papSync.progress.pagesProcessed} icon={FileText} />
              {papSync.startedAt && papSync.lastCompletedAt && (
                <SyncStat
                  label="duration"
                  value={`${Math.round((new Date(papSync.lastCompletedAt).getTime() - new Date(papSync.startedAt).getTime()) / 1000)}s`}
                  icon={Timer}
                />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
